import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography
} from '@material-ui/core'
import { FC, Fragment, useCallback, useState } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { formValueSelector } from 'redux-form'
import { openDialog } from '../../../actions/dialogs'
import { createShowings } from '../../../actions/showings'
import { OpenDialog } from '../../../actions/types'
import Container from '../../../components/Container'
import { selectApartmentById } from '../../../selectors/apartments'
import {
  getApplicationsNameIdPairs,
  selectApplicationsByApartmentId,
  selectApplicationsByStatus,
  selectSelectedApplications
} from '../../../selectors/applications'
import { selectFormValues } from '../../../selectors/form'
import { selectContentIdFromPathname } from '../../../selectors/router'
import { selectIsAdminUser } from '../../../selectors/users'
import i18n from '../../../translations/i18n'
import { Apartment } from '../../../types/apartments'
import { ApplicationsByStatus } from '../../../types/applications'
import { FormShowing } from '../../../types/showings'
import { CreateShowingsFormState, StateInterface } from '../../../types/states'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import ApplicantsToggleGroup from '../Edit/ApplicantsToggleGroup'
import ShowingCard from '../Overview/ShowingCard'
import ConfirmDialog from './ConfirmDialog'

interface ShowingsInviteProps extends WithTranslation {
  t: i18n.TFunction
}

interface StateProps {
  isAdmin: boolean
  apartment?: Apartment
  applicationsByStatus?: ApplicationsByStatus
  selectedApplications: string[]
  applicationNameIdPairs?: any[]
  createShowingsForm: any
  showings: FormShowing[]
}
interface DispatchProps {
  openDialog: OpenDialog
  createShowingsAction: (formData: CreateShowingsFormState) => void
}
type ComponentProps = ShowingsInviteProps & StateProps & DispatchProps

const ShowingsInvite: FC<ComponentProps> = ({
  isAdmin,
  apartment,
  t,
  applicationsByStatus,
  createShowingsForm,
  selectedApplications,
  createShowingsAction,
  showings,
  openDialog
}) => {
  const [confirmImmediately, setConfirmImmediately] = useState<boolean>(false)

  const handleConfirmImmediately = useCallback(() => {
    setConfirmImmediately((_state: boolean) => !_state)
  }, [])

  const startDate = createShowingsForm?.showings[0]?.startDate

  const applicantsToggleGroupProps = {
    applicationsByStatus: applicationsByStatus,
    confirmedApplications: [],
    showingDateTime: startDate
  }

  return (
    <Fragment>
      <Container>
        <Container>
          <Typography variant='h5' gutterBottom>
            {apartment &&
              apartment.information &&
              `${apartment.information.address}, ${apartment.information.postalCode} ${apartment.information.postalArea} `}
          </Typography>
          {Array.isArray(showings) &&
            showings.map((element: FormShowing) => {
              return (
                <ShowingCard
                  key={element.startDate}
                  cardDate={element.startDate}
                  t={t}
                  useCase={'delete'}
                />
              )
            })}
          <Typography gutterBottom variant='h6'>
            {t(`showings.invite.selectApplicants`)}
          </Typography>
          <Typography gutterBottom variant='body2'>
            {t(`showings.invite.inviteInfo`)}
          </Typography>

          <ApplicantsToggleGroup {...applicantsToggleGroupProps} />
        </Container>
        {isAdmin && Array.isArray(showings) && showings.length === 1 && (
          <FormControlLabel
            control={
              <Checkbox
                value={confirmImmediately}
                onChange={handleConfirmImmediately}
              />
            }
            label={t(`showings.invite.confirmImmediately`)}
          />
        )}
        <Button
          disabled={
            selectedApplications.length === 0 ||
            !showings ||
            showings.length === 0
          }
          onClick={() => {
            createShowingsAction({
              showings: showings,
              confirmImmediately
            })
          }}
          color='primary'
          variant='contained'
          fullWidth
        >
          {`${t('application.inviteApplicants')} (${
            selectedApplications.length
          })`}
        </Button>
      </Container>
      <ConfirmDialog />
    </Fragment>
  )
}

const selector = formValueSelector('CreateShowingsForm')

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)

  return {
    isAdmin: selectIsAdminUser(state),
    apartment: apartmentId
      ? selectApartmentById(apartmentId)(state)
      : undefined,
    applicationsByStatus: apartmentId
      ? selectApplicationsByStatus(
          selectApplicationsByApartmentId(apartmentId)(state)
        )
      : undefined,
    selectedApplications: selectSelectedApplications(state),
    applicationNameIdPairs: apartmentId
      ? getApplicationsNameIdPairs(apartmentId, state)
      : undefined,
    createShowingsForm: selectFormValues('CreateShowingsForm')(state),
    showings: selector(state, 'showings')
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openDialog: (id: string) => dispatch(openDialog(id)),
  createShowingsAction: bindSubmitActionToPromise(dispatch, createShowings)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ShowingsInvite))
